// src/features/auth/pages/PasswordResetRequestPage.jsx
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import AppIcon from "../../../assets/AppIcon.svg";

import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { env } from "../../../app/config/env";
import AuthApi from "../api/authApi";

export default function PasswordResetRequestPage() {
  const navigate = useNavigate();
  const appName = env.APP_NAME;

  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [nuevoPassword, setNuevoPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [codeEnabled, setCodeEnabled] = useState(false);
  const [sending, setSending] = useState(false);
  const [changing, setChanging] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const scrollBoxRef = useRef(null);
  const sectionConfirmRef = useRef(null);
  const emailInputRef = useRef(null);

  const busy = sending || changing;

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const closeToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((t) => ({ ...t, open: false }));
  };

  function focusNoScroll(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }

  function scrollToSection(sectionEl) {
    const box = scrollBoxRef.current;
    if (!box || !sectionEl) return;

    const top = sectionEl.offsetTop - 6;
    box.scrollTo({ top, behavior: "smooth" });
  }

  const emailValido = useMemo(() => {
    return /\S+@\S+\.\S+/.test(email.trim());
  }, [email]);

  const canSend = useMemo(() => {
    return emailValido && !busy;
  }, [emailValido, busy]);

  const canChange = useMemo(() => {
    return (
      codeEnabled &&
      emailValido &&
      codigo.trim().length === 6 &&
      nuevoPassword.trim().length >= 4 &&
      !busy
    );
  }, [codeEnabled, emailValido, codigo, nuevoPassword, busy]);

  async function handleSendCode() {
    if (!canSend) return;

    setSending(true);

    try {
      await AuthApi.requestPasswordReset({
        email: email.trim(),
      });

      setCodeEnabled(true);
      showToast("success", "Si existe el email, se envió un código. Revisá tu mail y SPAM.");

      setTimeout(() => {
        scrollToSection(sectionConfirmRef.current);
      }, 200);
    } catch (err) {
      setCodeEnabled(false);
      showToast("error", err?.message || "No se pudo enviar el código. Probá de nuevo.");
    } finally {
      setSending(false);
    }
  }

  async function handleChangePassword() {
    if (!canChange) return;

    setChanging(true);

    try {
      const res = await AuthApi.confirmPasswordReset({
        email: email.trim(),
        code: codigo.trim(),
        newPassword: nuevoPassword,
      });

      showToast("success", res?.detail || "Contraseña actualizada correctamente.");
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      const status = err?.status;

      if (status === 400) {
        showToast(
          "error",
          err?.message || "Código incorrecto o vencido. Generá uno nuevo."
        );
      } else {
        showToast("error", err?.message || "No se pudo cambiar la contraseña.");
      }
    } finally {
      setChanging(false);
    }
  }

  function handleGenerateNewCode() {
    setCodigo("");
    setNuevoPassword("");
    setCodeEnabled(false);

    const box = scrollBoxRef.current;
    if (box) {
      box.scrollTo({ top: 0, behavior: "smooth" });
    }

    setTimeout(() => {
      focusNoScroll(emailInputRef.current);
    }, 450);
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(230, 230, 234, 1)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ position: "absolute", top: 20, left: 20 }}
        >
          <Box
            component="img"
            src={AppIcon}
            alt={`${appName} icon`}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              objectFit: "contain",
              bgcolor: "transparent",
            }}
          />
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
            {appName}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Box
            sx={{
              width: "440px",
              maxWidth: "92vw",
              bgcolor: "rgba(191, 198, 225, 1)",
              borderRadius: "30px",
              p: "20px",
              boxShadow:
                "inset 0px 0px 4px rgba(117, 117, 138, 0.5), 0px 0px 10px rgba(165, 165, 178, 0.5)",
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
                <Box
                  sx={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "42px",
                    bgcolor: "rgba(14, 50, 149, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <VpnKeyIcon sx={{ width: "32px", height: "32px", color: "white" }} />
                </Box>

                <Box
                  sx={{
                    px: "20px",
                    py: "10px",
                    bgcolor: "rgba(14, 50, 149, 1)",
                    borderRadius: "33px",
                    boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                      fontSize: "22px",
                      fontWeight: 600,
                      color: "rgba(254, 254, 255, 1)",
                      textAlign: "center",
                    }}
                  >
                    Recuperá tu contraseña
                  </Typography>
                </Box>
              </Stack>

              <Box
                ref={scrollBoxRef}
                sx={{
                  width: "100%",
                  maxHeight: "54vh",
                  overflowY: "auto",
                  pr: "6px",
                  scrollBehavior: "smooth",
                }}
              >
                <Stack spacing={2.25} sx={{ width: "100%" }}>
                  <Box>
                    <Stack spacing={1.5} sx={{ width: "100%", my: 10 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "rgba(2, 8, 23, 1)",
                          textAlign: "center",
                        }}
                      >
                        Ingresá tu mail para generar el código:
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                          fontSize: "12px",
                          color: "rgba(2, 8, 23, 1)",
                          textAlign: "center",
                        }}
                      >
                        El código se enviará al correo asociado a tu cuenta.
                      </Typography>

                      <TextField
                        inputRef={emailInputRef}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setCodigo("");
                          setNuevoPassword("");
                          setCodeEnabled(false);
                        }}
                        placeholder="Email"
                        fullWidth
                        autoComplete="email"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon
                                sx={{ width: 24, height: 24, color: "rgba(2, 8, 23, 1)" }}
                              />
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: "rgba(247, 247, 248, 1)",
                            borderRadius: "10px",
                            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "16px",
                            fontWeight: 300,
                            color: "rgba(2, 8, 23, 1)",
                          },
                        }}
                        disabled={busy}
                      />

                      <Button
                        type="button"
                        disabled={!canSend}
                        onClick={handleSendCode}
                        sx={{
                          px: "20px",
                          py: "10px",
                          bgcolor: "rgba(18, 64, 191, 1)",
                          borderRadius: "33px",
                          textTransform: "none",
                          "&:hover": { bgcolor: "rgba(14, 50, 149, 1)" },
                          "&.Mui-disabled": {
                            bgcolor: "rgba(18, 64, 191, 0.35)",
                            color: "rgba(255,255,255,0.9)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "rgba(254, 254, 255, 1)",
                          }}
                        >
                          {sending ? "Enviando..." : "Enviar código"}
                        </Typography>
                      </Button>
                    </Stack>
                  </Box>

                  <Box ref={sectionConfirmRef}>
                    <Stack spacing={1.25} sx={{ width: "100%", pt: 1 }}>
                      <Typography
                        onClick={handleGenerateNewCode}
                        sx={{
                          fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                          fontSize: "15px",
                          fontWeight: 600,
                          color: "rgba(14, 50, 149, 1)",
                          textAlign: "center",
                          cursor: busy ? "default" : "pointer",
                          userSelect: "none",
                          opacity: busy ? 0.6 : 1,
                        }}
                      >
                        Generar nuevo código
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "rgba(2, 8, 23, 1)",
                          textAlign: "center",
                        }}
                      >
                        Completá estos campos para cambiar la contraseña:
                      </Typography>

                      <TextField
                        value={codigo}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, "").slice(0, 6);
                          setCodigo(onlyNumbers);
                        }}
                        placeholder="Código"
                        fullWidth
                        autoComplete="one-time-code"
                        disabled={!codeEnabled || busy}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyIcon sx={{ width: 24, height: 24, color: "rgba(2, 8, 23, 1)" }} />
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: "rgba(247, 247, 248, 1)",
                            borderRadius: "10px",
                            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "16px",
                            fontWeight: 300,
                            color: "rgba(2, 8, 23, 1)",
                          },
                        }}
                        helperText={
                          codeEnabled
                            ? "Si no lo ves, revisá SPAM. Si necesitás, generá un nuevo código."
                            : "Primero enviá el código para habilitar esta sección."
                        }
                        FormHelperTextProps={{
                          sx: {
                            m: 0,
                            mt: "6px",
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "13px",
                            color: "rgba(2, 8, 23, 0.7)",
                          },
                        }}
                      />

                      <TextField
                        value={nuevoPassword}
                        onChange={(e) => {
                          setNuevoPassword(e.target.value);
                        }}
                        type={showPassword ? "text" : "password"}
                        placeholder="Nueva contraseña"
                        fullWidth
                        autoComplete="new-password"
                        disabled={!codeEnabled || busy}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ width: 24, height: 24, color: "rgba(2, 8, 23, 1)" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((s) => !s)}
                                edge="end"
                                sx={{ width: 24, height: 24 }}
                                disabled={!codeEnabled || busy}
                                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                              >
                                {showPassword ? (
                                  <VisibilityOffIcon sx={{ width: 24, height: 24 }} />
                                ) : (
                                  <VisibilityIcon sx={{ width: 24, height: 24 }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: "rgba(247, 247, 248, 1)",
                            borderRadius: "10px",
                            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "16px",
                            fontWeight: 300,
                            color: "rgba(2, 8, 23, 1)",
                          },
                        }}
                      />

                      <Button
                        type="button"
                        disabled={!canChange}
                        onClick={handleChangePassword}
                        sx={{
                          px: "20px",
                          py: "10px",
                          bgcolor: "rgba(18, 64, 191, 1)",
                          borderRadius: "33px",
                          textTransform: "none",
                          "&:hover": { bgcolor: "rgba(14, 50, 149, 1)" },
                          "&.Mui-disabled": {
                            bgcolor: "rgba(18, 64, 191, 0.35)",
                            color: "rgba(255,255,255,0.9)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "rgba(254, 254, 255, 1)",
                          }}
                        >
                          {changing ? "Cambiando..." : "Cambiar contraseña"}
                        </Typography>
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Typography
                sx={{
                  fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                  fontSize: "15px",
                  fontWeight: 400,
                  color: "rgba(2, 8, 23, 1)",
                  textAlign: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  mt: "6px",
                }}
                onClick={() => navigate("/login")}
              >
                Volver al login
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{
            borderRadius: "14px",
            boxShadow: "0px 10px 30px rgba(0,0,0,.18)",
            fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
            fontWeight: 600,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}